import { useCallback } from 'react';
import { ErrorCode, FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useAudioFileContext } from '../../context';
import { MAX_AUDIO_DURATION, MIN_AUDIO_DURATION } from './constants';
import { errorMessage } from './helpers';

type PropsType = {
  onSuccess: (audioFile: File) => void;
};

const useUploadAudioValidation = ({ onSuccess }: PropsType) => {
  const { t } = useTranslation();

  const { setValidationError } = useAudioFileContext();

  const validateAudioType = useCallback(
    async (audioFile: File) => {
      const validAudioTypes = ['audio/wav'];
      const isValidType = validAudioTypes.includes(audioFile.type);

      if (!isValidType) {
        throw new Error(t(errorMessage(ErrorCode.FileInvalidType)));
      }
    },
    [t]
  );

  const validateAudio = useCallback(
    async (audio: HTMLAudioElement, audioFile: File) => {
      const { duration } = audio;
      validateAudioType(audioFile);
      if (duration < MIN_AUDIO_DURATION) {
        throw new Error(t(errorMessage(ErrorCode.FileTooSmall)));
      }
      if (duration > MAX_AUDIO_DURATION) {
        throw new Error(t(errorMessage(ErrorCode.FileTooLarge)));
      }
    },
    [t, validateAudioType]
  );

  const loadAudio = useCallback(
    (audioFile: File) => {
      const audioSrc = URL.createObjectURL(audioFile);
      const audio = new Audio();
      audio.addEventListener('loadedmetadata', async () => {
        URL.revokeObjectURL(audioSrc);
        try {
          await validateAudio(audio, audioFile);
        } catch (error) {
          setValidationError(error as Error);
          return;
        }
        setValidationError(null);
        onSuccess(audioFile);
      });
      audio.src = audioSrc;
    },
    [onSuccess, setValidationError, validateAudio]
  );

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) {
        return;
      }

      const audioFile = acceptedFiles[0];
      loadAudio(audioFile);
    },
    [loadAudio]
  );

  const onDropRejected = useCallback(
    (rejectedFiles: FileRejection[]) => {
      if (!rejectedFiles.length) {
        return;
      }
      if (rejectedFiles.length > 1) {
        setValidationError(new Error(t(errorMessage(ErrorCode.TooManyFiles))));
        return;
      }
      const { errors } = rejectedFiles[0];
      if (!errors.length) {
        return;
      }

      const message = errorMessage(errors[0].code);
      setValidationError(new Error(t(message)));
    },
    [t, setValidationError]
  );

  return {
    onDropAccepted,
    onDropRejected,
  };
};

export default useUploadAudioValidation;
